import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr'
import { ABP_AUTH_TOKEN_STORAGE, BUXUS_API_BASE_URL } from '../../platform/abp/config'
import { normalizePeople } from '../pagePeopleModel'
import type {
  BoardVersion,
  CameraBeat,
  ExerciseRun,
  ScenarioComment,
  ScenarioMapRef,
  ScenarioObject,
  ScenarioPerson,
  TimelineEvent,
  WizardOrigin,
} from '../types'
import { parseStudioClock, type StudioClock } from './scenarioClock'

export type SessionFieldPatch = {
  title?: string
  intro?: string | null
  summary?: string
  timezone?: string
  displayUnits?: 'imperial' | 'metric'
  timelineTimeFormat?: 'hm' | 'hms'
  showTimeZone?: boolean
  omitContactDetailsFromExports?: boolean
  rolePlayEnabled?: boolean
  boardComplexity?: 'easy' | 'default' | 'everything'
  sessionType?: string | null
  published?: boolean
  activeMapId?: string
  /** Board offer list — grows only when a pick promotes a plate onto the session. */
  maps?: ScenarioMapRef[]
  /** Board home framing (Set Location). */
  camera?: { lng: number; lat: number; zoom: number }
  wizardOrigin?: WizardOrigin | null
  /** Advertised meeting window. Null clears. */
  scheduledStartTime?: string | null
  scheduledEndTime?: string | null
  /** Story clock end. Null clears. Absent on older boards, which use a one-hour bar. */
  storyEndTime?: string | null
  /** False keeps the End field as a time only. Null clears. */
  storyEndDateSet?: boolean | null
  /** Designated / primary owner (ABP Guid). Null clears. */
  primaryOwnerUserId?: string | null
  /** Shared Exercise take phase — room strip chrome for everyone. */
  exerciseTakeNumber?: number
  exerciseTakeStopped?: boolean
  /** Take-pause freeze point (story ms). Null clears for the room. */
  instantReplayPauseMs?: number | null
  /** Instant Replay tape playing (spectators mirror the lower-third). */
  instantReplayPlaying?: boolean
  /** Instant Replay entered via scrub (not plain Pause). */
  instantReplayEngaged?: boolean
  /** Room tape review target run id (null clears). */
  tapeReviewRunId?: string | null
  /** Plan versions (seeds) — all session types may carry these. */
  boardVersions?: BoardVersion[]
  selectedVersionId?: string | null
  /** Working-board story spine (narration + sync rows). */
  timeline?: TimelineEvent[]
  /** Optional camera injects keyed to timeline stamps. */
  cameraBeats?: CameraBeat[] | null
  /** Attempt history (Run N + optional slug). */
  exerciseRuns?: ExerciseRun[]
  /** Master room chat (YouTube-style live thread). */
  roomChat?: ScenarioComment[]
  /** Shared 3…2…1 before an official take starts (null clears). */
  runStartCountdown?: number | null
  /** Wall ISO when countdown hits 0 — peers tick locally from this. */
  runStartCountdownEndsAt?: string | null
  /** Leftover session JSON — admin/developer seats replaced these toggles. */
  oversightRevealPresence?: boolean
  /** Leftover session JSON — admin/developer seats replaced these toggles. */
  oversightAllowControl?: boolean
  /** Live take: trainer forces plumes for the room. Carries across runs. */
  runRoomPlumes?: boolean
  /** Who last set runRoomPlumes (display name). */
  runRoomPlumesBy?: string
}

/** Peer Board `startRun` — one committed take (runs + optional seed). Not a full Snapshot. */
export type StartRunPatch = {
  selectedVersionId: string
  exerciseTakeNumber: number
  exerciseRuns: ExerciseRun[]
  seedApplied: boolean
  anchorStoryTimeIso?: string
  rate?: number
  objects?: ScenarioObject[]
  timeline?: TimelineEvent[]
  camera?: { lng: number; lat: number; zoom: number }
  cameraBeats?: CameraBeat[]
  activeMapId?: string
}

export type StartRunResult = {
  seq: number
  takeNumber: number
  runId: string
  seedApplied: boolean
}

/** Peer Board `stopRun` — take stopped + tape id + pause head. Not a full Snapshot. */
export type StopRunPatch = {
  exerciseTakeNumber: number
  exerciseTakeStopped: true
  tapeReviewRunId: string
  stoppedStoryElapsedMs: number
  exerciseRuns: ExerciseRun[]
  selectedVersionId?: string
  anchorStoryTimeIso?: string
  rate?: number
}

export type StopRunResult = {
  seq: number
  takeNumber: number
  runId: string
  stoppedStoryElapsedMs: number
}

/** Lean working-board patch from hub ApplyBoardSeed (not full SessionJson). */
export type ApplyBoardSeedPatch = {
  selectedVersionId: string
  objects: ScenarioObject[]
  timeline: TimelineEvent[]
  camera?: { lng: number; lat: number; zoom: number }
  cameraBeats?: CameraBeat[]
  activeMapId?: string
}

export type ScenarioHubHandlers = {
  onSnapshot: (raw: unknown) => void
  /** instanceId is required on current servers; omit only for legacy Clock payloads. */
  onClock: (clock: StudioClock, instanceId?: string, clientTrace?: string | null) => void
  onSessionFields: (fields: SessionFieldPatch, seq: number) => void
  /** Peer version flip — working board from server seed (GroupExcept sender). */
  onApplyBoardSeed: (patch: ApplyBoardSeedPatch, seq: number) => void
  /** Peer official Start Run — seed + run row together (GroupExcept sender). */
  onStartRun: (patch: StartRunPatch, seq: number) => void
  /** Peer official Stop Run — take stopped + tape review (GroupExcept sender). */
  onStopRun: (patch: StopRunPatch, seq: number) => void
  onAudienceType: (audienceType: 'private' | 'public', seq: number, instanceId?: string) => void
  onAllowLinkGuests: (allow: boolean, seq: number, instanceId?: string) => void
  onPeople: (
    people: ScenarioPerson[],
    seq: number,
    actor: string | null,
    instanceId?: string | null,
  ) => void
  onObject: (object: ScenarioObject, seq: number) => void
  onObjectRemoved: (objectId: string, seq: number) => void
  onWatchFrame: (frame: {
    active: boolean
    lng?: number
    lat?: number
    zoom?: number
    pointerLng?: number
    pointerLat?: number
    pointerDown?: boolean
    clickSeq?: number
    hoverId?: string
    hoverTip?: string
    grab?: boolean
    cursor?: 'pointer' | 'grab' | 'grabbing'
    focusId?: string
    dragId?: string
    dragLng?: number
    dragLat?: number
    edit?: boolean
    who?: string
    whoName?: string
    whoEmail?: string
    /** Done-review Presenter story playhead (ms from story start). */
    storyElapsedMs?: number
    storyRunning?: boolean
    /** Presenter's personal Show plumes, so followers can match it. */
    surfacePlumes?: boolean
  }) => void
  /** Room Presenter email, or null/empty when cleared. */
  onPresenter: (email: string | null) => void
  onComments: (comments: ScenarioComment[], seq: number) => void
  onPresence: (
    action: 'join' | 'leave' | 'sync',
    name: string,
    email: string | null,
    online: { email: string; name: string }[] | null,
  ) => void
  onDeleted: (instanceId: string) => void
  onInvited: (raw: unknown, actor: string | null) => void
  /** SignalR link — UI blanks strip transport while down / reconnecting. */
  onConnectionState?: (state: 'connected' | 'reconnecting' | 'disconnected') => void
}

let connection: HubConnection | null = null
let handlers: ScenarioHubHandlers | null = null
let joinedId: string | null = null
let joinedSeq = 0
/** Bumped on stop / a newer ensure — persist retries must not keep starting. */
let connectGen = 0

/**
 * SignalR default automatic-reconnect delays (then give up).
 * Used both after a live drop and for an explicit Try again `start()`.
 */
export const STUDIO_HUB_RECONNECT_DELAYS_MS = [0, 2000, 10000, 30000] as const

const HUB_LOG = '[scenario-hub]'
const TX_DEBUG_KEY = 'buxus.sc.txDebug'
const CLOCK_TRACE_CAP = 500

/**
 * Closed-loop clock debug: every Seek/Pause/PreviewPlay/Resume/Start/Stop/SetRate
 * gets a short `clientTrace` that the hub echoes on Clock and writes in server logs.
 * Dump with `copy(JSON.stringify(__scenarioClockTrace(), null, 2))` in the console
 * on both browsers, then grep the same `trace=` strings in the API log.
 */
export type ClockTraceSide = 'tx' | 'rx' | 'local' | 'apply'
export type ClockTraceEntry = {
  t: number
  iso: string
  side: ClockTraceSide
  kind: string
  trace: string
  instanceId?: string | null
  detail?: Record<string, unknown>
}

const clockTraceRing: ClockTraceEntry[] = []
let clockTraceSeq = 0

function padTraceSeq(n: number): string {
  return n.toString(36).padStart(3, '0')
}

/** Short correlation id — appears in browser dump + `[ScenarioHub] … trace=` server logs. */
export function newClientTrace(tag = 'clk'): string {
  clockTraceSeq = (clockTraceSeq + 1) % 46656 // 36^3
  const rand = Math.random().toString(36).slice(2, 6)
  const id = `${tag}.${padTraceSeq(clockTraceSeq)}.${rand}`
  return id.length <= 64 ? id : id.slice(0, 64)
}

export function noteClockTrace(
  side: ClockTraceSide,
  kind: string,
  trace: string,
  detail?: Record<string, unknown>,
  instanceId?: string | null,
): void {
  const t = Date.now()
  const entry: ClockTraceEntry = {
    t,
    iso: new Date(t).toISOString(),
    side,
    kind,
    trace: trace || '',
    ...(instanceId != null ? { instanceId } : {}),
    ...(detail && Object.keys(detail).length ? { detail } : {}),
  }
  clockTraceRing.push(entry)
  while (clockTraceRing.length > CLOCK_TRACE_CAP) clockTraceRing.shift()
  // Always visible — this is the closed loop, not optional TX spam.
  logHub(`clock-trace ${side}`, kind, `trace=${trace || '—'}`, detail ?? '')
}

export function clockTraceSnapshot(): {
  connectionId: string | null
  joinedId: string | null
  entries: ClockTraceEntry[]
} {
  return {
    connectionId: connection?.connectionId ?? null,
    joinedId,
    entries: clockTraceRing.slice(),
  }
}

export function clearClockTrace(): void {
  clockTraceRing.length = 0
}

/**
 * True when this tab sent `trace` on a clock RPC. A second Owner's Pause/Seek
 * echoes their id, not ours, and must not be treated as our stale play echo.
 */
export function clientSentClockTrace(trace: string): boolean {
  const id = trace.trim()
  if (!id) return false
  for (let i = clockTraceRing.length - 1; i >= 0; i--) {
    const entry = clockTraceRing[i]
    if (entry.side === 'tx' && entry.trace === id) return true
  }
  return false
}

/** Live TX counters — `localStorage.setItem('buxus.sc.txDebug','1')` then watch console / `__scenarioHubTx()`. */
const txStats = {
  inFlight: 0,
  watchQueued: 0,
  watchSent: 0,
  seekSent: 0,
  clockSent: 0,
  lastOkAt: 0,
  lastOkMethod: '',
  lastFailAt: 0,
  lastFailMethod: '',
  lastFailMsg: '',
  lastBackpressureAt: 0,
}

function txDebugEnabled(): boolean {
  try {
    return localStorage.getItem(TX_DEBUG_KEY) === '1'
  } catch {
    return false
  }
}

export function hubTxSnapshot(): {
  connectionState: string
  connectionId: string | null
  joinedId: string | null
  inFlight: number
  watchQueued: number
  watchSent: number
  seekSent: number
  clockSent: number
  lastOkAt: number
  lastOkMethod: string
  lastFailAt: number
  lastFailMethod: string
  lastFailMsg: string
  ageOkMs: number | null
  ageFailMs: number | null
} {
  const now = Date.now()
  return {
    connectionState: connection?.state ?? 'no-connection',
    connectionId: connection?.connectionId ?? null,
    joinedId,
    inFlight: txStats.inFlight,
    watchQueued: txStats.watchQueued,
    watchSent: txStats.watchSent,
    seekSent: txStats.seekSent,
    clockSent: txStats.clockSent,
    lastOkAt: txStats.lastOkAt,
    lastOkMethod: txStats.lastOkMethod,
    lastFailAt: txStats.lastFailAt,
    lastFailMethod: txStats.lastFailMethod,
    lastFailMsg: txStats.lastFailMsg,
    ageOkMs: txStats.lastOkAt ? now - txStats.lastOkAt : null,
    ageFailMs: txStats.lastFailAt ? now - txStats.lastFailAt : null,
  }
}

function noteTxOk(method: string): void {
  txStats.lastOkAt = Date.now()
  txStats.lastOkMethod = method
  if (method === 'Seek') txStats.seekSent++
  else if (method === 'SetWatchFrame') txStats.watchSent++
  else if (
    method === 'PreviewPlay' ||
    method === 'Pause' ||
    method === 'Resume' ||
    method === 'Start' ||
    method === 'SetRate'
  ) {
    txStats.clockSent++
  }
}

function noteTxFail(method: string, err: unknown): void {
  txStats.lastFailAt = Date.now()
  txStats.lastFailMethod = method
  txStats.lastFailMsg =
    err instanceof Error ? err.message : typeof err === 'string' ? err : String(err)
}

function logHub(...args: unknown[]): void {
  console.info(HUB_LOG, ...args)
}

function logHubWarn(...args: unknown[]): void {
  console.warn(HUB_LOG, ...args)
}

try {
  const w = window as unknown as {
    __scenarioHubTx?: () => ReturnType<typeof hubTxSnapshot>
    __scenarioClockTrace?: () => ReturnType<typeof clockTraceSnapshot>
    __scenarioClockTraceClear?: () => void
  }
  w.__scenarioHubTx = hubTxSnapshot
  w.__scenarioClockTrace = clockTraceSnapshot
  w.__scenarioClockTraceClear = clearClockTrace
} catch {
  /* SSR / non-browser */
}

function apiOrigin(): string {
  return BUXUS_API_BASE_URL.replace(/\/api\/?$/, '')
}

function accessToken(): string {
  return localStorage.getItem(ABP_AUTH_TOKEN_STORAGE)?.trim() ?? ''
}

function summarizeClock(clock: StudioClock): string {
  return `seq=${clock.seq} ${clock.lifecycle}/${clock.clockStatus} rate=${clock.rate} story=${clock.anchorStoryTimeIso ?? '—'} actual=${clock.anchorActualTime ?? '—'}`
}

function bind(conn: HubConnection): void {
  conn.on('Snapshot', (raw: unknown) => {
    const body = raw && typeof raw === 'object' ? (raw as { id?: unknown; clock?: { seq?: unknown } }) : {}
    logHub('event Snapshot', `id=${String(body.id ?? '?')}`, `seq=${String(body.clock?.seq ?? '?')}`)
    handlers?.onSnapshot(raw)
  })
  conn.on('Clock', (raw: unknown) => {
    const body =
      raw && typeof raw === 'object'
        ? (raw as {
            clock?: unknown
            instanceId?: unknown
            id?: unknown
            clientTrace?: unknown
            ClientTrace?: unknown
          })
        : {}
    const clock = parseStudioClock(body.clock ?? raw)
    const instanceId =
      typeof body.instanceId === 'string'
        ? body.instanceId
        : typeof body.id === 'string'
          ? body.id
          : undefined
    const clientTrace =
      typeof body.clientTrace === 'string'
        ? body.clientTrace
        : typeof body.ClientTrace === 'string'
          ? body.ClientTrace
          : null
    logHub(
      'event Clock',
      instanceId ? `id=${instanceId}` : 'id=?',
      summarizeClock(clock),
      clientTrace ? `trace=${clientTrace}` : 'trace=—',
    )
    noteClockTrace(
      'rx',
      'Clock',
      clientTrace ?? '',
      {
        seq: clock.seq,
        lifecycle: clock.lifecycle,
        status: clock.clockStatus,
        rate: clock.rate,
        story: clock.anchorStoryTimeIso,
        actual: clock.anchorActualTime,
      },
      instanceId,
    )
    handlers?.onClock(clock, instanceId, clientTrace)
  })
  conn.on('Board', (raw: unknown) => {
    const body = raw && typeof raw === 'object' ? (raw as { kind?: unknown; seq?: unknown; fields?: unknown }) : {}
    const fields = body.fields && typeof body.fields === 'object' ? (body.fields as SessionFieldPatch) : null
    const keys = fields ? Object.keys(fields).join(',') : ''
    logHub('event Board', `kind=${String(body.kind ?? '?')}`, `seq=${String(body.seq ?? '?')}`, keys)
    if (body.kind === 'setSessionFields' && fields) {
      handlers?.onSessionFields(fields, Number(body.seq ?? 0) || 0)
    }
    if (body.kind === 'setAudienceType' && fields) {
      const audience = (fields as { audienceType?: unknown }).audienceType
      const instanceId = (fields as { id?: unknown }).id
      if (audience === 'public' || audience === 'private') {
        handlers?.onAudienceType(
          audience,
          Number(body.seq ?? 0) || 0,
          typeof instanceId === 'string' ? instanceId : undefined,
        )
      }
    }
    if (body.kind === 'setAllowLinkGuests' && fields) {
      const allow = (fields as { allowLinkGuests?: unknown }).allowLinkGuests === true
      const instanceId = (fields as { id?: unknown }).id
      handlers?.onAllowLinkGuests(
        allow,
        Number(body.seq ?? 0) || 0,
        typeof instanceId === 'string' ? instanceId : undefined,
      )
    }
    if (body.kind === 'watchFrame' && fields) {
      const active = (fields as { active?: unknown }).active === true
      const lng = (fields as { lng?: unknown }).lng
      const lat = (fields as { lat?: unknown }).lat
      const zoom = (fields as { zoom?: unknown }).zoom
      const pointerLng = (fields as { pointerLng?: unknown }).pointerLng
      const pointerLat = (fields as { pointerLat?: unknown }).pointerLat
      const clickSeq = (fields as { clickSeq?: unknown }).clickSeq
      const hoverId = (fields as { hoverId?: unknown }).hoverId
      const hoverTip = (fields as { hoverTip?: unknown }).hoverTip
      const focusId = (fields as { focusId?: unknown }).focusId
      const cursor = (fields as { cursor?: unknown }).cursor
      const whoEmailRaw = (fields as { whoEmail?: unknown }).whoEmail
      handlers?.onWatchFrame({
        active,
        lng: typeof lng === 'number' ? lng : undefined,
        lat: typeof lat === 'number' ? lat : undefined,
        zoom: typeof zoom === 'number' ? zoom : undefined,
        pointerLng: typeof pointerLng === 'number' ? pointerLng : undefined,
        pointerLat: typeof pointerLat === 'number' ? pointerLat : undefined,
        pointerDown: (fields as { pointerDown?: unknown }).pointerDown === true,
        clickSeq: typeof clickSeq === 'number' ? clickSeq : undefined,
        hoverId: typeof hoverId === 'string' && hoverId ? hoverId : undefined,
        hoverTip: typeof hoverTip === 'string' && hoverTip ? hoverTip : undefined,
        grab: (fields as { grab?: unknown }).grab === true,
        cursor: cursor === 'pointer' || cursor === 'grab' || cursor === 'grabbing' ? cursor : undefined,
        focusId: typeof focusId === 'string' ? focusId : undefined,
        edit: (fields as { edit?: unknown }).edit === true,
        who: typeof (fields as { who?: unknown }).who === 'string'
          ? (fields as { who: string }).who
          : undefined,
        whoName: typeof (fields as { whoName?: unknown }).whoName === 'string'
          ? (fields as { whoName: string }).whoName
          : undefined,
        whoEmail:
          typeof whoEmailRaw === 'string' && whoEmailRaw.trim()
            ? whoEmailRaw.trim().toLowerCase()
            : undefined,
        dragId: typeof (fields as { dragId?: unknown }).dragId === 'string'
          ? (fields as { dragId: string }).dragId
          : undefined,
        dragLng: typeof (fields as { dragLng?: unknown }).dragLng === 'number'
          ? (fields as { dragLng: number }).dragLng
          : undefined,
        dragLat: typeof (fields as { dragLat?: unknown }).dragLat === 'number'
          ? (fields as { dragLat: number }).dragLat
          : undefined,
        storyElapsedMs:
          typeof (fields as { storyElapsedMs?: unknown }).storyElapsedMs === 'number'
            ? (fields as { storyElapsedMs: number }).storyElapsedMs
            : undefined,
        storyRunning:
          typeof (fields as { storyRunning?: unknown }).storyRunning === 'boolean'
            ? (fields as { storyRunning: boolean }).storyRunning
            : undefined,
        surfacePlumes:
          typeof (fields as { surfacePlumes?: unknown }).surfacePlumes === 'boolean'
            ? (fields as { surfacePlumes: boolean }).surfacePlumes
            : undefined,
      })
    }
    if (body.kind === 'setObject' && fields) {
      const raw = (fields as { objectJson?: unknown }).objectJson
      const text = typeof raw === 'string' ? raw : JSON.stringify(raw ?? null)
      try {
        const parsed = JSON.parse(text) as ScenarioObject
        if (parsed && typeof parsed.id === 'string' && parsed.id) {
          handlers?.onObject(parsed, Number(body.seq ?? 0) || 0)
        }
      } catch {
        logHubWarn('event Board setObject unreadable')
      }
    }
    if (body.kind === 'removeObject' && fields) {
      const objectId = (fields as { objectId?: unknown }).objectId
      if (typeof objectId === 'string' && objectId) {
        handlers?.onObjectRemoved(objectId, Number(body.seq ?? 0) || 0)
      }
    }
    if (body.kind === 'startRun' && fields) {
      const parseJson = (raw: unknown): unknown => {
        if (typeof raw === 'string') {
          try {
            return JSON.parse(raw)
          } catch {
            return null
          }
        }
        return raw
      }
      const vidRaw = (fields as { selectedVersionId?: unknown }).selectedVersionId
      const versionId = typeof vidRaw === 'string' ? vidRaw.trim() : ''
      const takeRaw = (fields as { exerciseTakeNumber?: unknown }).exerciseTakeNumber
      const takeNumber =
        typeof takeRaw === 'number' && Number.isFinite(takeRaw) ? Math.max(0, Math.floor(takeRaw)) : 0
      const runsRaw = parseJson((fields as { exerciseRunsJson?: unknown }).exerciseRunsJson)
      const seedApplied = (fields as { seedApplied?: unknown }).seedApplied === true
      const objectsRaw = parseJson((fields as { objectsJson?: unknown }).objectsJson)
      const timelineRaw = parseJson((fields as { timelineJson?: unknown }).timelineJson)
      const cameraRaw = parseJson((fields as { cameraJson?: unknown }).cameraJson)
      const beatsRaw = parseJson((fields as { cameraBeatsJson?: unknown }).cameraBeatsJson)
      const mapRaw = (fields as { activeMapId?: unknown }).activeMapId
      const storyRaw = (fields as { anchorStoryTimeIso?: unknown }).anchorStoryTimeIso
      const rateRaw = (fields as { rate?: unknown }).rate
      const camera =
        cameraRaw &&
        typeof cameraRaw === 'object' &&
        typeof (cameraRaw as { lng?: unknown }).lng === 'number' &&
        typeof (cameraRaw as { lat?: unknown }).lat === 'number' &&
        typeof (cameraRaw as { zoom?: unknown }).zoom === 'number'
          ? {
              lng: (cameraRaw as { lng: number }).lng,
              lat: (cameraRaw as { lat: number }).lat,
              zoom: (cameraRaw as { zoom: number }).zoom,
            }
          : undefined
      if (versionId && takeNumber > 0 && Array.isArray(runsRaw)) {
        handlers?.onStartRun(
          {
            selectedVersionId: versionId,
            exerciseTakeNumber: takeNumber,
            exerciseRuns: runsRaw as ExerciseRun[],
            seedApplied,
            ...(typeof storyRaw === 'string' && storyRaw.trim()
              ? { anchorStoryTimeIso: storyRaw.trim() }
              : {}),
            ...(typeof rateRaw === 'number' && Number.isFinite(rateRaw) ? { rate: rateRaw } : {}),
            ...(seedApplied && Array.isArray(objectsRaw) && Array.isArray(timelineRaw)
              ? {
                  objects: objectsRaw as ScenarioObject[],
                  timeline: timelineRaw as TimelineEvent[],
                  ...(camera ? { camera } : {}),
                  ...(Array.isArray(beatsRaw) ? { cameraBeats: beatsRaw as CameraBeat[] } : {}),
                  ...(typeof mapRaw === 'string' && mapRaw.trim()
                    ? { activeMapId: mapRaw.trim() }
                    : {}),
                }
              : {}),
          },
          Number(body.seq ?? 0) || 0,
        )
      } else {
        logHubWarn('event Board startRun unreadable')
      }
    }
    if (body.kind === 'stopRun' && fields) {
      const parseJson = (raw: unknown): unknown => {
        if (typeof raw === 'string') {
          try {
            return JSON.parse(raw)
          } catch {
            return null
          }
        }
        return raw
      }
      const takeRaw = (fields as { exerciseTakeNumber?: unknown }).exerciseTakeNumber
      const takeNumber =
        typeof takeRaw === 'number' && Number.isFinite(takeRaw) ? Math.max(0, Math.floor(takeRaw)) : 0
      const tapeRaw = (fields as { tapeReviewRunId?: unknown }).tapeReviewRunId
      const tapeReviewRunId = typeof tapeRaw === 'string' ? tapeRaw.trim() : ''
      const stopMsRaw = (fields as { stoppedStoryElapsedMs?: unknown }).stoppedStoryElapsedMs
      const stoppedStoryElapsedMs =
        typeof stopMsRaw === 'number' && Number.isFinite(stopMsRaw)
          ? Math.max(0, Math.floor(stopMsRaw))
          : 0
      const runsRaw = parseJson((fields as { exerciseRunsJson?: unknown }).exerciseRunsJson)
      const vidRaw = (fields as { selectedVersionId?: unknown }).selectedVersionId
      const storyRaw = (fields as { anchorStoryTimeIso?: unknown }).anchorStoryTimeIso
      const rateRaw = (fields as { rate?: unknown }).rate
      if (takeNumber > 0 && tapeReviewRunId && Array.isArray(runsRaw)) {
        handlers?.onStopRun(
          {
            exerciseTakeNumber: takeNumber,
            exerciseTakeStopped: true,
            tapeReviewRunId,
            stoppedStoryElapsedMs,
            exerciseRuns: runsRaw as ExerciseRun[],
            ...(typeof vidRaw === 'string' && vidRaw.trim()
              ? { selectedVersionId: vidRaw.trim() }
              : {}),
            ...(typeof storyRaw === 'string' && storyRaw.trim()
              ? { anchorStoryTimeIso: storyRaw.trim() }
              : {}),
            ...(typeof rateRaw === 'number' && Number.isFinite(rateRaw) ? { rate: rateRaw } : {}),
          },
          Number(body.seq ?? 0) || 0,
        )
      } else {
        logHubWarn('event Board stopRun unreadable')
      }
    }
    if (body.kind === 'applyBoardSeed' && fields) {
      const vidRaw = (fields as { selectedVersionId?: unknown }).selectedVersionId
      const versionId = typeof vidRaw === 'string' ? vidRaw.trim() : ''
      const parseJson = (raw: unknown): unknown => {
        if (typeof raw === 'string') {
          try {
            return JSON.parse(raw)
          } catch {
            return null
          }
        }
        return raw
      }
      const objectsRaw = parseJson((fields as { objectsJson?: unknown }).objectsJson)
      const timelineRaw = parseJson((fields as { timelineJson?: unknown }).timelineJson)
      const cameraRaw = parseJson((fields as { cameraJson?: unknown }).cameraJson)
      const beatsRaw = parseJson((fields as { cameraBeatsJson?: unknown }).cameraBeatsJson)
      const mapRaw = (fields as { activeMapId?: unknown }).activeMapId
      if (versionId && Array.isArray(objectsRaw) && Array.isArray(timelineRaw)) {
        const camera =
          cameraRaw &&
          typeof cameraRaw === 'object' &&
          typeof (cameraRaw as { lng?: unknown }).lng === 'number' &&
          typeof (cameraRaw as { lat?: unknown }).lat === 'number' &&
          typeof (cameraRaw as { zoom?: unknown }).zoom === 'number'
            ? {
                lng: (cameraRaw as { lng: number }).lng,
                lat: (cameraRaw as { lat: number }).lat,
                zoom: (cameraRaw as { zoom: number }).zoom,
              }
            : undefined
        handlers?.onApplyBoardSeed(
          {
            selectedVersionId: versionId,
            objects: objectsRaw as ScenarioObject[],
            timeline: timelineRaw as TimelineEvent[],
            ...(camera ? { camera } : {}),
            ...(Array.isArray(beatsRaw) ? { cameraBeats: beatsRaw as CameraBeat[] } : {}),
            ...(typeof mapRaw === 'string' && mapRaw.trim()
              ? { activeMapId: mapRaw.trim() }
              : {}),
          },
          Number(body.seq ?? 0) || 0,
        )
      } else {
        logHubWarn('event Board applyBoardSeed unreadable')
      }
    }
    if (body.kind === 'setComments' && fields) {
      const raw = (fields as { commentsJson?: unknown }).commentsJson
      const text = typeof raw === 'string' ? raw : JSON.stringify(raw ?? [])
      try {
        const parsed = JSON.parse(text) as unknown
        if (Array.isArray(parsed)) {
          handlers?.onComments(parsed as ScenarioComment[], Number(body.seq ?? 0) || 0)
        }
      } catch {
        logHubWarn('event Board setComments unreadable')
      }
    }
    if (body.kind === 'presence' && fields) {
      const action = (fields as { action?: unknown }).action
      if (action === 'sync') {
        const raw = (fields as { people?: unknown }).people
        const online = Array.isArray(raw)
          ? raw.flatMap((item) => {
              if (!item || typeof item !== 'object') return []
              const email = (item as { email?: unknown }).email
              const name = (item as { name?: unknown }).name
              if (typeof email !== 'string' || !email.trim()) return []
              return [{ email: email.trim(), name: typeof name === 'string' ? name.trim() : '' }]
            })
          : []
        handlers?.onPresence('sync', '', null, online)
      } else {
        const nameRaw = (fields as { name?: unknown }).name
        const emailRaw = (fields as { email?: unknown }).email
        const email = typeof emailRaw === 'string' ? emailRaw.trim() : ''
        const name = typeof nameRaw === 'string' ? nameRaw.trim() : ''
        // Leave must work with email alone (disconnect often lacks a display name).
        if ((action === 'join' || action === 'leave') && (name || email)) {
          handlers?.onPresence(action, name || email, email || null, null)
        }
      }
    }
    if (body.kind === 'presenter' && fields) {
      const raw = (fields as { email?: unknown }).email
      const email = typeof raw === 'string' ? raw.trim().toLowerCase() : ''
      handlers?.onPresenter(email || null)
    }
    if (body.kind === 'deleted' && fields) {
      const instanceId = (fields as { instanceId?: unknown }).instanceId
      if (typeof instanceId === 'string' && instanceId) handlers?.onDeleted(instanceId)
    }
    if (body.kind === 'invited' && fields) {
      const actorRaw = (fields as { actor?: unknown }).actor
      const actor = typeof actorRaw === 'string' ? actorRaw.trim() : ''
      handlers?.onInvited(fields, actor || null)
    }
    if (body.kind === 'setPeople' && fields) {
      const raw = (fields as { peopleJson?: unknown }).peopleJson
      const text = typeof raw === 'string' ? raw : JSON.stringify(raw ?? [])
      try {
        const parsed = JSON.parse(text) as unknown
        if (Array.isArray(parsed)) {
          const actorRaw = (fields as { actor?: unknown }).actor
          const actor = typeof actorRaw === 'string' ? actorRaw.trim() : ''
          const instRaw = (fields as { instanceId?: unknown }).instanceId
          const instanceId = typeof instRaw === 'string' ? instRaw.trim() : ''
          handlers?.onPeople(
            normalizePeople(parsed),
            Number(body.seq ?? 0) || 0,
            actor || null,
            instanceId || null,
          )
        }
      } catch {
        logHubWarn('event Board setPeople unreadable')
      }
    }
  })
  conn.onreconnecting((err) => {
    if (connection !== conn) return
    logHubWarn('reconnecting', err?.message ?? '')
    handlers?.onConnectionState?.('reconnecting')
  })
  conn.onreconnected((connectionId) => {
    if (connection !== conn) return
    logHub('reconnected', connectionId ?? conn.connectionId)
    handlers?.onConnectionState?.('connected')
    if (!joinedId) return
    // Always Snapshot after reconnect (lastSeq=0). Board-only seq bumps must not
    // convince Join we are caught up when Clock playheads were dropped mid-gap.
    joinedSeq = 0
    logHub('→ Join', joinedId, 'lastSeq=0', '(Snapshot after reconnect)')
    void conn.invoke('Join', joinedId, 0).catch((err: unknown) => {
      logHubWarn('← Join failed after reconnect', err)
    })
  })
  conn.onclose((err) => {
    // Ignore close from a connection we already replaced (Try again / stop).
    if (connection !== conn) return
    logHubWarn('closed', err?.message ?? 'clean')
    handlers?.onConnectionState?.('disconnected')
  })
}

async function startScenarioHubOnce(): Promise<HubConnection> {
  if (connection && connection.state !== HubConnectionState.Disconnected) {
    logHub('reuse', connection.state, connection.connectionId ?? '(no id yet)')
    return connection
  }
  const url = `${apiOrigin()}/signalr-hubs/scenarios`
  const headers: Record<string, string> = {}
  if (apiOrigin().includes('ngrok')) headers['ngrok-skip-browser-warning'] = 'true'
  const token = accessToken()
  logHub('connecting', url, token ? 'bearer present' : 'no bearer token')
  connection = new HubConnectionBuilder()
    .withUrl(url, {
      accessTokenFactory: accessToken,
      headers,
      // Bearer via accessTokenFactory — not cookies. Avoids requiring
      // Access-Control-Allow-Credentials on Azure App Service CORS (portal blade).
      withCredentials: false,
    })
    .withAutomaticReconnect([...STUDIO_HUB_RECONNECT_DELAYS_MS])
    .build()
  bind(connection)
  try {
    await connection.start()
  } catch (err) {
    logHubWarn('connect failed', err)
    const failed = connection
    connection = null
    if (failed.state !== HubConnectionState.Disconnected) {
      await failed.stop().catch(() => {})
    }
    throw err
  }
  logHub('connected', connection.connectionId, connection.state)
  handlers?.onConnectionState?.('connected')
  return connection
}

export async function ensureScenarioHub(
  next: ScenarioHubHandlers,
  opts?: { persist?: boolean },
): Promise<HubConnection> {
  handlers = next
  if (connection && connection.state !== HubConnectionState.Disconnected) {
    logHub('reuse', connection.state, connection.connectionId ?? '(no id yet)')
    return connection
  }
  const gen = ++connectGen
  const delays = opts?.persist ? STUDIO_HUB_RECONNECT_DELAYS_MS : ([0] as const)
  let lastErr: unknown
  for (let i = 0; i < delays.length; i++) {
    if (gen !== connectGen) throw new Error('Scenario hub connect cancelled.')
    const wait = delays[i] ?? 0
    if (wait) {
      handlers?.onConnectionState?.('reconnecting')
      await new Promise((resolve) => setTimeout(resolve, wait))
      if (gen !== connectGen) throw new Error('Scenario hub connect cancelled.')
    }
    try {
      return await startScenarioHubOnce()
    } catch (err) {
      lastErr = err
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('Scenario hub is not connected.')
}

async function waitForScenarioHub(): Promise<HubConnection> {
  const start = Date.now()
  while (!connection || connection.state === HubConnectionState.Connecting || connection.state === HubConnectionState.Reconnecting) {
    if (Date.now() - start > 8000) {
      logHubWarn('not connected after 8s', connection?.state ?? 'no connection')
      throw new Error('Scenario hub is not connected.')
    }
    await new Promise((resolve) => setTimeout(resolve, 50))
  }
  if (connection.state !== HubConnectionState.Connected) {
    throw new Error('Scenario hub is not connected.')
  }
  return connection
}

export async function joinScenarioInstance(instanceId: string, lastSeq: number): Promise<void> {
  const sameBoard = joinedId === instanceId
  // Leave the prior group first so in-flight Clock events cannot land on the new selection.
  if (joinedId && !sameBoard) {
    await leaveScenarioInstance().catch(() => {})
  }
  joinedId = instanceId
  // After Leave we missed every Fields patch. The catalog seq can already match the
  // server while local roomChat is still the invite-time / last-visit copy —
  // Join(highSeq) would skip Snapshot and the later lines never arrive.
  joinedSeq = sameBoard ? lastSeq : 0
  await invokeHub('Join', instanceId, joinedSeq)
}

/** Leave the board group without joining another (hub / templates). Stops Watch + presence. */
export async function leaveScenarioInstance(): Promise<void> {
  if (!joinedId) return
  const leaving = joinedId
  joinedId = null
  joinedSeq = 0
  await invokeHub('Leave')
  logHub('left', leaving)
}

/** Currently joined board id (null when parked / between leave and join). */
export function hubJoinedInstanceId(): string | null {
  return joinedId
}

/**
 * Advance the Join catch-up watermark from **Clock / Snapshot only**.
 * Do not call from Board (objects/people/fields): those bump instance seq without
 * carrying playhead, and a reconnect Join(highSeq) would skip Snapshot while the
 * follower is still on a stale head (missed Seeks / Start Run chain).
 */
export function noteScenarioSeq(seq: number): void {
  if (seq > joinedSeq) joinedSeq = seq
}

/** While clock RPCs are in flight / just sent, Watch pump yields so Share cannot starve Seek. */
let clockPriorityUntil = 0

function noteClockPriority(): void {
  clockPriorityUntil = Date.now() + 120
}

async function invokeHub<T = void>(method: string, ...args: unknown[]): Promise<T> {
  const conn = await waitForScenarioHub()
  // SetWatchFrame is high-frequency (map camera) — keep the console readable unless txDebug.
  const quiet = method === 'SetWatchFrame' && !txDebugEnabled()
  const isClock =
    method === 'Seek' ||
    method === 'Pause' ||
    method === 'Resume' ||
    method === 'PreviewPlay' ||
    method === 'Start' ||
    method === 'SetRate'
  if (isClock) noteClockPriority()
  if (!quiet) logHub('→', method, ...args, `conn=${conn.connectionId ?? '?'}`)
  txStats.inFlight++
  // Host caps parallel hub invokes per client at 8 — Watch spam used to fill the pipe
  // and starve Seek/PreviewPlay (P freezes while A still looks fine locally).
  if (txStats.inFlight >= 6) {
    const now = Date.now()
    if (now - txStats.lastBackpressureAt > 2000) {
      txStats.lastBackpressureAt = now
      logHubWarn('backpressure', `inFlight=${txStats.inFlight}`, method, hubTxSnapshot())
    }
  }
  try {
    const result = await conn.invoke(method, ...args)
    noteTxOk(method)
    if (isClock) noteClockPriority()
    if (!quiet) logHub('←', method, 'ok')
    return result as T
  } catch (err) {
    noteTxFail(method, err)
    logHubWarn('←', method, 'failed', err)
    throw err
  } finally {
    txStats.inFlight = Math.max(0, txStats.inFlight - 1)
  }
}

type WatchFrameArg =
  | { active: false }
  | {
      active: true
      lng: number
      lat: number
      zoom: number
      pointerLng?: number
      pointerLat?: number
      pointerDown?: boolean
      clickSeq?: number
      hoverId?: string
      hoverTip?: string
      grab?: boolean
      cursor?: 'pointer' | 'grab' | 'grabbing'
      focusId?: string
      dragId?: string
      dragLng?: number
      dragLat?: number
      edit?: boolean
      who?: string
      whoName?: string
      storyElapsedMs?: number
      storyRunning?: boolean
      /** Stamped at send from the sharer's personal Show plumes. */
      surfacePlumes?: boolean
    }

/** Coalesce: at most one SetWatchFrame in flight; always send the latest pending pose. */
let watchFrameInFlight = false
let watchFramePending: { instanceId: string; frame: WatchFrameArg } | null = null
let watchFramePump: Promise<void> | null = null
/** Last frame actually sent — skip identical spam (Share+Preview used to flood every elapsed tick). */
let watchFrameLastSent: { instanceId: string; key: string } | null = null

function watchFrameKey(instanceId: string, frame: WatchFrameArg): string {
  if (!frame.active) return `${instanceId}|off`
  return [
    instanceId,
    frame.lng?.toFixed(5),
    frame.lat?.toFixed(5),
    frame.zoom?.toFixed(2),
    frame.pointerLng?.toFixed(5) ?? '',
    frame.pointerLat?.toFixed(5) ?? '',
    frame.pointerDown ? '1' : '0',
    frame.dragId ?? '',
    frame.dragLng?.toFixed(5) ?? '',
    frame.dragLat?.toFixed(5) ?? '',
    frame.edit ? 'e' : '',
    frame.focusId ?? '',
    frame.hoverId ?? '',
    frame.cursor ?? '',
    // Story playhead: 100ms buckets — Preview Share must not send on every ticker ms.
    frame.storyElapsedMs != null ? String(Math.round(frame.storyElapsedMs / 100)) : '',
    frame.storyRunning == null ? '' : frame.storyRunning ? '1' : '0',
    frame.surfacePlumes == null ? '' : frame.surfacePlumes ? '1' : '0',
  ].join('|')
}

/** Sharer's personal Show plumes, stamped onto every active Watch frame at send. */
let outgoingWatchPlumes = true
let watchPlumesResend = false
let lastWatchJob: {
  instanceId: string
  frame: Extract<WatchFrameArg, { active: true }>
} | null = null

function stampWatchPlumes(frame: WatchFrameArg): WatchFrameArg {
  if (!frame.active) return frame
  return { ...frame, surfacePlumes: outgoingWatchPlumes }
}

/** Keep the bit current. Does not publish a frame by itself. */
export function setOutgoingWatchPlumes(on: boolean): void {
  outgoingWatchPlumes = on
}

/**
 * Re-send the last active Watch frame so followers pick up a plume-pref change
 * while the presenter is sitting still. No-op until a frame has been sent.
 */
export function resendWatchPlumes(): void {
  if (!lastWatchJob?.frame.active) return
  watchPlumesResend = true
  ensureWatchFramePump()
}

function ensureWatchFramePump(): void {
  if (watchFramePump) return
  watchFramePump = (async () => {
    if (watchFrameInFlight) return
    watchFrameInFlight = true
    try {
      while (watchFramePending || (watchPlumesResend && lastWatchJob?.frame.active)) {
        // Do not hold an invoke slot during this wait — Share used to await SetWatchFrame
        // RTT and starve PreviewPlay/Seek (A looked Previewing locally; P never got Clock).
        if (Date.now() < clockPriorityUntil) {
          await new Promise((r) => setTimeout(r, 16))
          continue
        }
        if (!watchFramePending && lastWatchJob?.frame.active) {
          watchPlumesResend = false
          watchFramePending = {
            instanceId: lastWatchJob.instanceId,
            frame: { ...lastWatchJob.frame },
          }
        }
        const job = watchFramePending
        if (!job) continue
        watchFramePending = null
        txStats.watchQueued = 0
        const frame = stampWatchPlumes(job.frame)
        const key = watchFrameKey(job.instanceId, frame)
        if (
          watchFrameLastSent &&
          watchFrameLastSent.instanceId === job.instanceId &&
          watchFrameLastSent.key === key
        ) {
          if (frame.active) watchPlumesResend = false
          continue
        }
        try {
          const conn = await waitForScenarioHub()
          // send = on-the-wire only; invoke waited for hub method completion and ate
          // MaximumParallelInvocationsPerClient slots under Share pointer traffic.
          await conn.send('SetWatchFrame', job.instanceId, frame)
          noteTxOk('SetWatchFrame')
          watchFrameLastSent = { instanceId: job.instanceId, key }
          if (frame.active) {
            watchPlumesResend = false
            lastWatchJob = {
              instanceId: job.instanceId,
              frame: job.frame as Extract<WatchFrameArg, { active: true }>,
            }
          } else {
            lastWatchJob = null
          }
          if (txDebugEnabled()) {
            logHub('→', 'SetWatchFrame', 'send', job.instanceId)
          }
          // Local cadence only (~30fps max) — not waiting on server.
          await new Promise((r) => setTimeout(r, 32))
        } catch (err) {
          noteTxFail('SetWatchFrame', err)
          logHubWarn('←', 'SetWatchFrame', 'send failed', err)
        }
      }
    } finally {
      watchFrameInFlight = false
    }
  })().finally(() => {
    watchFramePump = null
    // Frame landed in the gap between while-exit and pump clear — kick once more.
    if (watchFramePending || watchPlumesResend) ensureWatchFramePump()
  })
}

/** Wait until coalesced Watch frames drain — call before Upsert on pointer-up. */
export async function hubFlushWatchFrames(): Promise<void> {
  if (watchFramePending || watchFrameInFlight || watchPlumesResend) ensureWatchFramePump()
  const start = Date.now()
  while (watchFramePending || watchFrameInFlight || watchFramePump || watchPlumesResend) {
    if (Date.now() - start > 2000) {
      logHubWarn('hubFlushWatchFrames timed out', hubTxSnapshot())
      return
    }
    await (watchFramePump ?? new Promise((r) => setTimeout(r, 16)))
  }
}

export async function hubStart(
  instanceId: string,
  rate = 1,
  clientTrace?: string | null,
): Promise<string> {
  const trace = clientTrace?.trim() || newClientTrace('Start')
  noteClockTrace('tx', 'Start', trace, { rate }, instanceId)
  await invokeHub('Start', instanceId, rate, trace)
  return trace
}

/** Open Live Idle at story start — does not Play / Run. */
export async function hubGoLive(
  instanceId: string,
  rate = 1,
  clientTrace?: string | null,
): Promise<string> {
  const trace = clientTrace?.trim() || newClientTrace('GoLive')
  noteClockTrace('tx', 'GoLive', trace, { rate }, instanceId)
  await invokeHub('GoLive', instanceId, rate, trace)
  return trace
}

/** Shared Preview while Planned — does not Go Live (Lifecycle stays Planned). */
export async function hubPreviewPlay(
  instanceId: string,
  rate = 1,
  clientTrace?: string | null,
): Promise<string> {
  const trace = clientTrace?.trim() || newClientTrace('PrevPlay')
  noteClockTrace('tx', 'PreviewPlay', trace, { rate }, instanceId)
  await invokeHub('PreviewPlay', instanceId, rate, trace)
  return trace
}

export async function hubSetRate(
  instanceId: string,
  rate: number,
  clientTrace?: string | null,
): Promise<string> {
  const trace = clientTrace?.trim() || newClientTrace('SetRate')
  noteClockTrace('tx', 'SetRate', trace, { rate }, instanceId)
  await invokeHub('SetRate', instanceId, rate, trace)
  return trace
}

/**
 * IR tape: pauseTape clears playing; startTape sets playing — both on the Seek UPDATE.
 * @param forceAdmit Bypass hub storm gate (Start Run Seek→story-start).
 * @param pinHead Intentional IR jump (beat skip / scrub) — spectators snap via align seq.
 *   Distinct from forceAdmit (play/pause also force-admit but must not snap peers).
 * @returns false when the hub storm-dropped the Seek (retry); true when the room got a Clock.
 */
export async function hubSeek(
  instanceId: string,
  storyPlayheadIso: string,
  pauseTape = false,
  startTape = false,
  forceAdmit = false,
  clientTrace?: string | null,
  pinHead = false,
): Promise<boolean> {
  const trace = clientTrace?.trim() || newClientTrace('Seek')
  noteClockTrace(
    'tx',
    'Seek',
    trace,
    { story: storyPlayheadIso, pauseTape, startTape, forceAdmit, pinHead },
    instanceId,
  )
  const admitted = await invokeHub<boolean>(
    'Seek',
    instanceId,
    storyPlayheadIso,
    pauseTape,
    startTape,
    forceAdmit,
    trace,
    pinHead,
  )
  noteClockTrace(
    'tx',
    admitted === false ? 'Seek.dropped' : 'Seek.ok',
    trace,
    { admitted: admitted !== false },
    instanceId,
  )
  // Older hosts may still return void — treat as admitted.
  return admitted !== false
}

/** Optional storyPlayheadIso freezes the room on the driver’s click second (avoids RTT drift). */
export async function hubPause(
  instanceId: string,
  storyPlayheadIso?: string | null,
  clientTrace?: string | null,
): Promise<string> {
  const trace = clientTrace?.trim() || newClientTrace('Pause')
  noteClockTrace(
    'tx',
    'Pause',
    trace,
    { story: storyPlayheadIso ?? null },
    instanceId,
  )
  // Always pass story slot so clientTrace stays the third hub arg.
  await invokeHub('Pause', instanceId, storyPlayheadIso ?? null, trace)
  return trace
}

export async function hubResume(
  instanceId: string,
  clientTrace?: string | null,
): Promise<string> {
  const trace = clientTrace?.trim() || newClientTrace('Resume')
  noteClockTrace('tx', 'Resume', trace, {}, instanceId)
  await invokeHub('Resume', instanceId, trace)
  return trace
}

/**
 * Live playhead + running in one Clock. Replaces Seek-then-Resume.
 * `rate` null keeps the room rate (no separate SetRate re-anchor).
 */
export async function hubResumeAt(
  instanceId: string,
  storyPlayheadIso: string,
  rate?: number | null,
  clientTrace?: string | null,
): Promise<string> {
  const trace = clientTrace?.trim() || newClientTrace('ResumeAt')
  noteClockTrace(
    'tx',
    'ResumeAt',
    trace,
    { story: storyPlayheadIso, rate: rate ?? null },
    instanceId,
  )
  await invokeHub('ResumeAt', instanceId, storyPlayheadIso, rate ?? null, trace)
  return trace
}

/**
 * Official Exercise Start Run — one hub update (run + optional seed + clock at story start).
 * Peers hear Board `startRun` then one Clock. Not Fields → seed → Seek → Resume.
 */
export async function hubStartRun(
  instanceId: string,
  args: {
    versionId: string
    continueDirty?: boolean
    rate?: number
    runId: string
    runNumber: number
    slug?: string | null
    clientTrace?: string | null
  },
): Promise<StartRunResult> {
  const trace = args.clientTrace?.trim() || newClientTrace('StartRun')
  noteClockTrace(
    'tx',
    'StartRun',
    trace,
    {
      versionId: args.versionId,
      runId: args.runId,
      runNumber: args.runNumber,
      continueDirty: args.continueDirty === true,
      rate: args.rate ?? 1,
    },
    instanceId,
  )
  const raw = await invokeHub<unknown>(
    'StartRun',
    instanceId,
    args.versionId,
    args.continueDirty === true,
    args.rate ?? 1,
    args.runId,
    args.runNumber,
    args.slug ?? null,
    trace,
  )
  const rec = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const takeRaw = rec.takeNumber
  const takeNumber =
    typeof takeRaw === 'number' && Number.isFinite(takeRaw)
      ? Math.max(1, Math.floor(takeRaw))
      : args.runNumber
  return {
    seq: typeof rec.seq === 'number' ? rec.seq : 0,
    takeNumber,
    runId: typeof rec.runId === 'string' && rec.runId.trim() ? rec.runId.trim() : args.runId,
    seedApplied: rec.seedApplied === true,
  }
}

/**
 * Official Exercise Stop / story-end — one hub update (stop stamp + takeStopped + tape + Pause).
 * Peers hear Board `stopRun` then one Clock. Not Fields → Pause.
 */
export async function hubStopRun(
  instanceId: string,
  args?: {
    storyPlayheadIso?: string | null
    clientTrace?: string | null
  },
): Promise<StopRunResult> {
  const trace = args?.clientTrace?.trim() || newClientTrace('StopRun')
  const story = args?.storyPlayheadIso?.trim() || null
  noteClockTrace('tx', 'StopRun', trace, { story }, instanceId)
  const raw = await invokeHub<unknown>('StopRun', instanceId, story, trace)
  const rec = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const takeRaw = rec.takeNumber
  const takeNumber =
    typeof takeRaw === 'number' && Number.isFinite(takeRaw) ? Math.max(1, Math.floor(takeRaw)) : 1
  const stopMsRaw = rec.stoppedStoryElapsedMs
  return {
    seq: typeof rec.seq === 'number' ? rec.seq : 0,
    takeNumber,
    runId: typeof rec.runId === 'string' && rec.runId.trim() ? rec.runId.trim() : '',
    stoppedStoryElapsedMs:
      typeof stopMsRaw === 'number' && Number.isFinite(stopMsRaw)
        ? Math.max(0, Math.floor(stopMsRaw))
        : 0,
  }
}

export async function hubStop(
  instanceId: string,
  clientTrace?: string | null,
): Promise<string> {
  const trace = clientTrace?.trim() || newClientTrace('Stop')
  noteClockTrace('tx', 'Stop', trace, {}, instanceId)
  await invokeHub('Stop', instanceId, trace)
  return trace
}

export async function hubSetSessionFields(
  instanceId: string,
  fields: SessionFieldPatch,
): Promise<number> {
  // Do not noteScenarioSeq here — Fields Board seq is not a playhead watermark.
  // Take-phase Fields also piggyback Clock; that path notes seq in applyServerClock.
  return await invokeHub<number>('SetSessionFields', instanceId, fields)
}

/**
 * Write SetSessionFields during the key handler, before React paints.
 * invoke() yields first, so the plume map render can run before the socket write,
 * and it does not resolve until the hub method (and its row save) finishes.
 * send() resolves when the frame is on the wire.
 */
export function hubSendSessionFieldsNow(
  instanceId: string,
  fields: SessionFieldPatch,
): Promise<void> {
  const conn = connection
  if (!conn || conn.state !== HubConnectionState.Connected) {
    return hubSetSessionFields(instanceId, fields).then(() => undefined)
  }
  logHub('→', 'SetSessionFields', instanceId, fields, `conn=${conn.connectionId ?? '?'}`)
  return conn.send('SetSessionFields', instanceId, fields).then(
    () => {
      logHub('←', 'SetSessionFields', 'sent')
    },
    (err) => {
      logHubWarn('←', 'SetSessionFields', 'failed', err)
      throw err
    },
  )
}

/** Settings → story Start: server rebases SessionJson + appends session.rebase; room gets Snapshot. */
export async function hubRebaseStory(
  instanceId: string,
  storyStartTime: string,
  storyStartDateSet?: boolean | null,
): Promise<void> {
  await invokeHub('RebaseStory', instanceId, storyStartTime, storyStartDateSet ?? null)
}

/** Restore working board from a plan version seed; room gets Snapshot. */
export async function hubApplyBoardSeed(instanceId: string, versionId: string): Promise<void> {
  await invokeHub('ApplyBoardSeed', instanceId, versionId)
}

export async function hubSetAudienceType(instanceId: string, audienceType: 'private' | 'public'): Promise<void> {
  await invokeHub('SetAudienceType', instanceId, audienceType)
}

export async function hubSetAllowLinkGuests(instanceId: string, allow: boolean): Promise<void> {
  await invokeHub('SetAllowLinkGuests', instanceId, allow)
}

export async function hubSetComments(instanceId: string, comments: readonly ScenarioComment[]): Promise<void> {
  await invokeHub('SetComments', instanceId, comments)
}

export async function hubRespondToInvite(instanceId: string, status: 'joined' | 'declined'): Promise<void> {
  await invokeHub('RespondToInvite', instanceId, status)
}

export async function hubDeleteInstance(instanceId: string): Promise<void> {
  await invokeHub('DeleteInstance', instanceId)
}

export async function hubSetPeople(
  instanceId: string,
  people: readonly ScenarioPerson[],
  actor: string | null,
): Promise<void> {
  await invokeHub('SetPeople', instanceId, people, actor ?? '')
}

export type ScenarioInviteAttach = {
  fileName: string
  url: string
}

export async function hubSendInvite(
  instanceId: string,
  email: string,
  notice: 'invite' | 'resend' | 'reinvite' | 'nudge' | 'register',
  actor: string | null,
  introNote?: string | null,
  docKinds?: readonly ('plan' | 'trainer' | 'cheatsheet')[] | null,
  attaches?: readonly ScenarioInviteAttach[] | null,
): Promise<void> {
  await invokeHub(
    'SendInvite',
    instanceId,
    email,
    notice,
    actor ?? '',
    introNote ?? '',
    docKinds?.length ? [...docKinds] : null,
    attaches?.length ? attaches.map((a) => ({ fileName: a.fileName, url: a.url })) : null,
  )
}

export async function hubUpsertObject(instanceId: string, object: ScenarioObject): Promise<number> {
  return invokeHub<number>('UpsertObject', instanceId, object)
}

export async function hubRemoveObject(instanceId: string, objectId: string): Promise<number> {
  return invokeHub<number>('RemoveObject', instanceId, objectId)
}

export async function hubSetWatchFrame(
  instanceId: string,
  frame: WatchFrameArg,
): Promise<void> {
  // Merge with pending: a pointer-only frame (e.g. selectedId effect) must not wipe an
  // in-buffer drag pose — that left P's hand moving while the icon froze mid-drag.
  if (
    watchFramePending &&
    watchFramePending.instanceId === instanceId &&
    watchFramePending.frame.active &&
    frame.active
  ) {
    const prev = watchFramePending.frame
    if (
      prev.dragId &&
      prev.dragLng != null &&
      prev.dragLat != null &&
      (frame.dragId == null || frame.dragLng == null || frame.dragLat == null) &&
      frame.pointerDown
    ) {
      frame = {
        ...frame,
        dragId: prev.dragId,
        dragLng: prev.dragLng,
        dragLat: prev.dragLat,
      }
    }
  }
  watchFramePending = { instanceId, frame }
  txStats.watchQueued++
  ensureWatchFramePump()
}

/** Room Presenter for Auto-track. Pass null/empty to clear. */
export async function hubSetPresenter(instanceId: string, email: string | null): Promise<void> {
  await invokeHub('SetPresenter', instanceId, email ?? '')
}

export async function stopScenarioHub(): Promise<void> {
  connectGen += 1
  joinedId = null
  joinedSeq = 0
  if (!connection) return
  const conn = connection
  connection = null
  logHub('stopping', conn.connectionId ?? '(no id)')
  if (conn.state !== HubConnectionState.Disconnected) await conn.stop()
}
